import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { Loader2, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DateTimeUtil } from "../../utils";
import { RequestorDetails } from "../../components/notificationv2";
import { WorkflowRequest } from "../../types";
import { callPhpWorkflow } from "../../redux/features/common";
import { toast } from "react-toastify";

const SwapNotificationDetail: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { isNotificationDetailLoading, notificationDetail } = useSelector((state: RootState) => state.notification);
    const user = useSelector((state: RootState) => state.user.user);
    const { isCallingWorkflow } = useSelector((state: RootState) => state.common);

    const [selectedActors, setSelectedActors] = useState<{ [key: string]: string[] }>({});
    const [unselectedActors, setUnselectedActors] = useState<{ [key: string]: string[] }>({});
    const [notes, setNotes] = useState<string>("");

    const requestor = notificationDetail?.shift_swapping_details?.requestor;
    const requestorDetails = notificationDetail?.shift_swapping_details?.requestor_details;
    const selectionType = notificationDetail?.workflow_current_step_details?.selection_type || "single";
    const currentStepStatus = notificationDetail?.workflow_current_step_details?.current_status || "0";
    const actionConsecutiveDays = Number(notificationDetail?.shift_swapping_details?.days_in_swapping_block || "1");
    const getEffectiveActionConsecutiveDays = actionConsecutiveDays > 1 ? actionConsecutiveDays - 1 : undefined;
    // Add state for the reject modal
    const [showRejectModal, setShowRejectModal] = useState(false);
    const filteredWorkflowDetails =
        notificationDetail?.workflow_details?.flatMap((workflow) => {
            const actorMap = workflow.actor_json;
            console.log("actorMap", actorMap);
            const matchingActorEntry = Object.entries(actorMap || {}).find(
                ([employeeId, actor]) => employeeId == user.os_emp_account_id?.toString()
            );

            console.log("matchingActorEntry", matchingActorEntry);
            if (
                workflow.step_code === notificationDetail?.workflow_current_step_details?.step_code &&
                matchingActorEntry
            ) {
                const [employeeId, actor] = matchingActorEntry;
                return [
                    {
                        ...workflow,
                        actor_json: { [employeeId]: actor }, // retain only matching actor
                    },
                ];
            }

            return [];
        }) || [];

    useEffect(() => {
        const allActors: { [key: string]: string[] } = {};

        filteredWorkflowDetails.forEach((workflow) => {
            const actorEntry = Object.entries(workflow.actor_json || {})[0]; // we already narrowed it to one in previous step
            if (actorEntry) {
                const [employeeId, actor] = actorEntry;
                const scheduleId = actor.schedule_details?.[0]?.schedule_id ?? "";
                if (employeeId && scheduleId && !selectedActors[employeeId]) {
                    if (!allActors[employeeId]) allActors[employeeId] = [];
                    allActors[employeeId].push(scheduleId);
                }
            }
        });

        setUnselectedActors((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(allActors)) {
                return allActors;
            }
            return prev;
        });
    }, [filteredWorkflowDetails]);

    // Click handler to show the modal
    const handleRejectClick = () => {
        setShowRejectModal(true);
    };

    /**
     * Handles Approve action
     */
    const handleApprove = async () => {
        if (!selectedActors || Object.keys(selectedActors).length === 0) {
            toast.warning("Please select at least one actor to approve.");
            return;
        }
        const request: WorkflowRequest = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 1,
            actor_id: user?.os_emp_account_id?.toString(),
            user_account_id: user?.user_account_id?.toString(),
            reference_id: notificationDetail?.alert_json?.REF_ID?.toString(),
            result: { APPROVED: selectedActors, REJECTED: {} },
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            notes,
        };

        await dispatch(callPhpWorkflow(request));
        navigate("/notifications");
    };

    /**
     * Handles Reject action
     */
    const handleReject = async () => {
        if (!selectedActors || Object.keys(selectedActors).length === 0) {
            toast.warning("Please select at least one actor to approve.");
            return;
        }
        const request: WorkflowRequest = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 0,
            actor_id: user?.os_emp_account_id?.toString(),
            user_account_id: user?.user_account_id?.toString(),
            reference_id: notificationDetail?.alert_json?.REF_ID?.toString(),
            result: { REJECTED: selectedActors, APPROVED: {} },
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            notes,
        };

        await dispatch(callPhpWorkflow(request));
        navigate("/notifications");
    };

    const handleCheckboxChange = (employeeId: string, scheduleId: string) => {
        setSelectedActors((prev) => {
          const currentSelection = prev[employeeId] || [];
          const isSelected = currentSelection.includes(scheduleId);
      
          if (selectionType === "single") {
            return { [employeeId]: [scheduleId] };
          } else {
            const updatedSelection = isSelected
              ? currentSelection.filter((id) => id !== scheduleId)
              : [...currentSelection, scheduleId];
      
            if (updatedSelection.length === 0) {
              const { [employeeId]: _, ...rest } = prev; // omit the employeeId key
              return rest;
            }
      
            return {
              ...prev,
              [employeeId]: updatedSelection,
            };
          }
        });
      };
      

    useEffect(() => {
        console.log("selectedActors", selectedActors);
    }, [selectedActors]);

    if (isNotificationDetailLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2
                    className="w-8 h-8 text-g
                ray-600 animate-spin"
                />
            </div>
        );
    }

    // if (!notificationDetail && !isNotificationDetailLoading) {
    //     return (
    //         <div className="text-center text-gray-500 flex flex-col items-center gap-4 w-full">
    //             <p>No notification details available.</p>
    //             <button
    //                 onClick={() => navigate(-1)}
    //                 className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
    //             >
    //                 <ChevronLeft className="w-5 h-5" />
    //                 <span>Back</span>
    //             </button>
    //         </div>
    //     );
    // }

    return (
        <div className="w-full p-6 bg-white h-full overflow-y-auto">
            {/* Message Info */}
            {notificationDetail?.message_info && (
                <div className="border-b pb-4">
                    <div className="flex items-center">
                        <ChevronLeft className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                        <h2 className="ml-2 text-xl font-semibold text-gray-800">
                            {notificationDetail?.message_info.title}
                        </h2>
                    </div>
                    <p className="text-gray-600 mt-1 ml-8">{notificationDetail?.message_info.body}</p>
                </div>
            )}

            {/* Requestor Details */}
            <RequestorDetails
                requestor={requestor}
                requestorDetails={requestorDetails}
                effectiveConsecutiveDays={getEffectiveActionConsecutiveDays}
            />

            {/* Approved/Rejected Entries */}
            {["1", "-1"].includes(currentStepStatus) && (
                <div className="p-4 mt-6 rounded-lg h-94 overflow-y-auto">
                    <h3 className="text-lg font-semibold text-gray-700">
                        {currentStepStatus === "1" ? "Approvals" : "Rejections"}
                    </h3>
                    {filteredWorkflowDetails.map((workflow, index) => (
                        <div key={index} className="p-3 border-b">
                            {/* ✅ Approved Entries */}
                            {currentStepStatus === "1" &&
                                workflow.wf_task_acted_json?.RESULT?.APPROVED?.map((approval, idx) => (
                                    <div
                                        key={`approved-${idx}`}
                                        className="border border-green-200 bg-green-50 p-4 rounded-xl shadow-sm mb-4"
                                    >
                                        <div className="flex items-center gap-3 mb-2">
                                            {approval.image_url && (
                                                <img
                                                    src={approval.image_url}
                                                    alt={approval.name}
                                                    className="w-10 h-10 rounded-full object-cover border border-green-300"
                                                />
                                            )}
                                            <div>
                                                <p className="font-semibold text-green-700">
                                                    {approval.name ?? "Unknown"}
                                                </p>
                                                <p className="text-sm text-green-600">Approved</p>
                                            </div>
                                        </div>

                                        {workflow.wf_task_acted_json?.APPROVED_ON && (
                                            <p className="text-xs text-gray-600">
                                                <span className="font-medium">Approved On:</span>{" "}
                                                {DateTimeUtil.FormatWithDayjsUtc(
                                                    workflow.wf_task_acted_json.APPROVED_ON,
                                                    DateTimeUtil.DATETIME_FANCY_2
                                                )}
                                            </p>
                                        )}

                                        {workflow.wf_task_acted_json?.APPROVAL_NOTE && (
                                            <p className="text-xs text-gray-600 mt-1">
                                                <span className="font-medium">Note:</span>{" "}
                                                {workflow.wf_task_acted_json.APPROVAL_NOTE}
                                            </p>
                                        )}

                                        {approval.schedule_details && approval.schedule_details?.length > 0 && (
                                            <div className="mt-3 text-xs text-gray-700">
                                                <p className="font-semibold mb-1">Schedule Details:</p>
                                                <ul className="list-disc ml-4 space-y-1">
                                                    {approval.schedule_details.map((detail, sIdx) => (
                                                        <li key={sIdx}>
                                                            {detail.shift_name} –{" "}
                                                            {DateTimeUtil.formatDateRangeFancy(
                                                                detail.schedule_start_date!,
                                                                detail.schedule_end_date!
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}

                            {/* ❌ Rejected Entries */}
                            {currentStepStatus === "-1" &&
                                workflow.wf_task_acted_json?.RESULT?.REJECTED?.map((rejected, idx) => (
                                    <div
                                        key={`rejected-${idx}`}
                                        className="border border-red-200 bg-red-50 p-4 rounded-xl shadow-sm mb-4"
                                    >
                                        <div className="flex items-center gap-3 mb-2">
                                            {rejected.image_url && (
                                                <img
                                                    src={rejected.image_url}
                                                    alt={rejected.name}
                                                    className="w-10 h-10 rounded-full object-cover border border-red-300"
                                                />
                                            )}
                                            <div>
                                                <p className="font-semibold text-red-700">
                                                    {rejected.name ?? "Unknown"}
                                                </p>
                                                <p className="text-sm text-red-600">Rejected</p>
                                            </div>
                                        </div>

                                        {rejected.schedule_details && rejected.schedule_details?.length > 0 && (
                                            <div className="mt-3 text-xs text-gray-700">
                                                <p className="font-semibold mb-1">Schedule Details:</p>
                                                <ul className="list-disc ml-4 space-y-1">
                                                    {rejected.schedule_details.map((detail, sIdx) => (
                                                        <li key={sIdx}>
                                                            {detail.shift_name} ({detail.shift_code}) –{" "}
                                                            {detail.schedule_start_date} to {detail.schedule_end_date}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    ))}
                </div>
            )}

            {currentStepStatus === "0" && (
                <div className="p-4 mt-6">
                    <h3 className="text-lg font-semibold text-gray-700">Approve Shifts</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        {selectionType === "single"
                            ? "Select one actor & schedule"
                            : "Select multiple actors & schedules"}
                    </p>

                    {/* Workflow Actors & Schedule Options */}
                    <div className="space-y-4">
                        {filteredWorkflowDetails.map((workflow, index) => (
                            <div key={index} className="p-4 shadow-sm space-y-4">
                                {workflow.wf_event_json?.SWAPPING_DETAILS?.Approved?.map((approved, idx) => (
                                    <div key={idx} className="space-y-2">
                                        {/* Actor Info */}
                                        <div className="flex items-center gap-3 mb-4">
                                            {approved.image_url && (
                                                <img
                                                    src={approved.image_url}
                                                    alt={approved.name}
                                                    className="w-10 h-10 rounded-full object-cover border border-gray-300"
                                                />
                                            )}
                                            <p className="text-sm font-medium text-gray-800">{approved.name}</p>
                                        </div>

                                        {/* Schedule List */}
                                        <div className="space-y-2">
                                            {approved.schedule_details?.map((schedule, sIdx) => (
                                                <label
                                                    key={sIdx}
                                                    className="flex items-start gap-2 bg-white border rounded-md px-3 py-2 hover:shadow-sm transition"
                                                >
                                                    <input
                                                        type={selectionType === "single" ? "radio" : "checkbox"}
                                                        name={
                                                            selectionType === "single"
                                                                ? approved.employee_id || ""
                                                                : undefined
                                                        }
                                                        checked={selectedActors[approved.employee_id || ""]?.includes(
                                                            schedule.schedule_id || ""
                                                        )}
                                                        onChange={() =>
                                                            handleCheckboxChange(
                                                                approved.employee_id!,
                                                                schedule.schedule_id!
                                                            )
                                                        }
                                                        className="mt-1 w-4 h-4 accent-gray-700"
                                                    />
                                                    <div className="text-sm text-gray-700">
                                                        <p className="font-semibold">{schedule.shift_name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {DateTimeUtil.formatShortDate(
                                                                schedule.schedule_start_date!
                                                            )}{" "}
                                                            to{" "}
                                                            {DateTimeUtil.formatShortDate(schedule.schedule_end_date!)}
                                                            {getEffectiveActionConsecutiveDays !== undefined &&
                                                                getEffectiveActionConsecutiveDays > 1 && (
                                                                    <span>
                                                                        {" "}
                                                                        + {getEffectiveActionConsecutiveDays} days
                                                                    </span>
                                                                )}
                                                        </p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Notes Section */}
                    <textarea
                        className="w-full p-3 border border-gray-300 rounded-md mt-6 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Add notes (optional)..."
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />

                    {/* Approve & Reject Buttons */}
                    <div className="flex justify-end mt-6 gap-4">
                        {isCallingWorkflow ? (
                            <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
                        ) : (
                            <>
                                <button
                                    onClick={handleRejectClick}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={handleApprove}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                                >
                                    Approve
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showRejectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                        <h2 className="text-lg font-semibold mb-4">Reject Confirmation</h2>
                        <p className="mb-6 text-sm text-gray-700">
                            This will reject all shifts and close the request. Are you sure?
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    setShowRejectModal(false);
                                    await handleReject();
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Confirm Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SwapNotificationDetail;
